import enCA from 'date-fns/locale/en-CA'
import frCA from 'date-fns/locale/fr-CA'

export interface LanguageDictionary<T = any> {
	English: T
	French: T
}

export type LocalizedString = LanguageDictionary<string>

export type Language = keyof LanguageDictionary

export const AllLanguages = new Set<Language>(['English', 'French'])

export type LocaleName = 'en-CA' | 'fr-CA'

export const LocaleNames: LanguageDictionary<LocaleName> = {
	English: 'en-CA',
	French: 'fr-CA'
}

export const LocaleLanguages: { [k in LocaleName]: Language } = {
	'en-CA': 'English',
	'fr-CA': 'French'
}

export const Locales: LanguageDictionary<Locale> = {
	English: enCA, 
	French: frCA
}

export const LanguageIds: LanguageDictionary<number> = {
	English: 1,
	French: 2
}

// TODO: Possibly eliminate these reverse lookups.  
// Refactor so that models use Ids more than "keywords", 
// which legacy API originally supplied
export const LanguageIdLanguages: { [key: string]: Language } = {
	1: 'English',
	2: 'French'
}

const EnglishValidation = {
	FieldRequired: 'Required',
	FieldTooLong: 'Must be under {maxLength} characters',
	FieldTooSmall: 'Must be greater than {min}',
	FieldTooLarge: 'Must be less than {max}',
	FieldOutsideRange: 'Must be between {min} and {max}',
	InvalidPhoneNumber: 'Must be valid phone number',
	InvalidPhoneExtension: 'Must be valid phone extension',
	InvalidPostalCode: 'Must be valid postal code',
	InvalidEmail: 'Must be valid email',
}

export type LocalizedValidation = typeof EnglishValidation

const EnglishText = {
	Validation: EnglishValidation,
	Language: 'Language',
	English: 'English',
	French: 'French',
	HelpPageTitle: 'Help', 
	PageNotFound: '404: Page not found.',
	WorkshopIdNotFoundTemplate: '404: Workshop Id {workshopId} not found.',
	InPerson: 'In-Person',
	Online: 'Online',
	Workshops: 'Workshops',
	CapFull: 'Canadian Agricultural Partnership',
	OsciaFull: 'Ontario Soil and Crop Improvement Association',
	PleaseSelectAWorkshop: 'Please Select a Workshop',
	ClickAWorkshopForMoreInformation: 'Click a workshop for more information',
	HelpPageMessage: 'For more information, please see our ',
	HelpPageLinkText: 'help page',
	eEFPMessage: 
`
The Environmental Farm Plan (EFP) is an assessment voluntarily prepared by farm families to increase their environmental awareness in up to 23 different areas on their farm. Through the EFP process, farmers highlight their farm’s environmental strengths, identify areas of environmental concern, and set realistic action plans with time tables to improve environmental conditions. 

The eEFP is a convenient option for farmers looking to update their previously verified EFP workbook.  This is not a replacement for the in-person 2-day workshop, and farmers who are new to EFP or who have not participated for many years are encouraged to consider the in-person option which provides instruction from OSCIA and networking with fellow farmers. Farmers may also wish to bring a laptop to a one-day or two-day in-person workshop, and complete their EFP Workbook online using the eEFP.

To complete an eEFP, visit 
`, 
	eEFPLinkText: 'www.electronicefp.com',
	WorkshopLeader: 'Workshop Leader',
	SeatsAvailableTemplate: '{SeatsLeft} of {MaximumParticipants} seats available',
	Register: 'Register',
	Registered: 'Registered',
	Unregister: 'Unregister',
	Tentative: 'Tentative',
	Scheduled: 'Scheduled',
	Completed: 'Completed',
	Cancelled: 'Cancelled',
	InProgress: 'In Progress', 
	Day: 'Day',
	Date: 'Date',
	Start: 'Start',
	Finish: 'Finish',
	End: 'End',
	Room: 'Room',
	Status: 'Status',
	Farmland: 'Farmland',
	SelectDateRange: 'Select Date Range',
	SelectStartDate: 'Select Start Date',
	SelectEndDate: 'Select End Date',
	WorkshopType: 'Workshop Type',
	SelectWorkshopType: 'Select Workshop Type',
	SelectTier1: 'Select Tier 1',
	SelectTier2: 'Select Tier 2',
	CopyrightNoticeTemplate: "© King’s Printer for Ontario {year}",
	LoginRequired: 'Login Required',
	LoginRequiredMessage: 'Before you can register for a workshop, you must be logged in to OsciaPortal.org.  If you do not have an account, you must create one.',
	ProceedToOntarioProgramsNet: 'Proceed to OsciaPortal.org',
	Dashboard: 'Dashboard',
    Portal: "Portal",
	Login: 'Log In',
	Logout: 'Log Out',
	Continue: 'Continue',
	NoWorkshopsAvailableWithCurrentFilterSelections: 'There are no workshops scheduled within your selected criteria. Please adjust your filters to try your search again.',
	NoWorkshopsAvailableOfThisType: 'There are no workshops of this type currently scheduled.  Please select another workshop type or ',
	ContactYourWorkshopLeaderLinkText: 'contact your workshop leader',
	PleaseCorrectTheIndicatedErrors: 'Please correct the indicated errors', 
	ThisWorkshopIsTentativelyScheduled: 'This workshop is tentatively scheduled', 
	SessionExpirationTitle: 'Session Expiration',
	SessionExpirationWarning: 'Your session is about to expire. If you want to continue, please press "Continue"', 
	List: 'List', 
	Calendar: 'Calendar',
	View: 'View', 
	Today: 'Today', 
	PrerequisiteRequired: 'Prerequisite Required', 
	Confirm: 'Confirm',
	RegistrationCutoffNoticeTemplate: 'Only {friendlyDuration} remaining to register', 

	ParticipantDetails: 'Participant Details',
	FirstName: 'First Name',
	LastName: 'Last Name',
	AddParticipant: 'Add Participant', 
	Remove: 'Remove', 
	RemoveParticipant: 'Remove Participant', 
	AdditionalParticipant: 'Additional Participant',
	PreviousGuests: 'Previous Guests',
	Add: 'Add', 
	AddNew: 'Add New', 
	Province: 'Province',
	MunicipalityTier1: 'Municipality Tier 1',
	MunicipalityTier2: 'Municipality Tier 2',
	County: 'County',
	Municipality: 'Municipality',
	AddressLine1: 'Address Line 1',
	AddressLine2: 'Address Line 2',
	City: 'City',
	PostalCode: 'Postal Code',
	Email: 'Email',
	Phone1: 'Phone 1',
	Phone2: 'Phone 2',
	Ext: 'ext',
	IsCertifiedOrganic: 'Is Certified Organic',
	IsOrganicCertificationPending: 'Is Organic Certification Pending',
	CommodityCategory: 'Commodity Category',
	CommoditySubCategory: 'Commodity Sub Category',
	Watershed: 'Watershed',
	PreferredCorrespondenceLanguage: 'Preferred Correspondence Language',
	DoYouRequireAnyAccomodationsForAccessibility: 'Do you require any accommodations for accessibility?',
	PreviouslyCompletedWorkbooks: 'Previously Completed Workbooks',
	Cancel: 'Cancel',
	RegistrationSuccessful: 'You have successfully registered for this workshop',
	UnregistrationSuccessful: 'You have successfully unregistered from this workshop',
	UnregistrationConfirmationTitle: 'Unregistration Confirmation',
	UnregistrationConfirmationMessage: 'Are you sure you want to unregister from this workshop?',
	Admin: 'Admin', 
}

export type LocalizedText = typeof EnglishText

export const Text: LanguageDictionary<LocalizedText> = {
	English: EnglishText,
	French: {
		Validation: {
            FieldRequired: "Requis",
            FieldTooLong: "Doit être inférieur à {maxLength} caractères",
            FieldTooSmall: "Doit être supérieur à {min}",
            FieldTooLarge: "Doit être inférieur à {max}",
            FieldOutsideRange: "Doit être compris entre {min} et {max}",
            InvalidPhoneNumber: "Doit être un numéro de téléphone valide",
            InvalidPhoneExtension: "Doit être une extension de téléphone valide",
            InvalidPostalCode: "Doit être un code postal valide",
            InvalidEmail: "Doit être un courriel valide",
        },
        Language: "Langue",
        English: "anglais",
        French: "français",
		HelpPageTitle: "Page d'aide",
		PageNotFound: "404 : Page non trouvée.",
		WorkshopIdNotFoundTemplate: "404: identifiant d'atelier {workshopId} introuvable",
        InPerson: "En personne",
        Online: "En ligne",
        Workshops: "Ateliers",
        CapFull: "Partenariat canadien pour l'agriculture",
        OsciaFull: "Association pour l'amélioration des sols et des récoltes de l'Ontario",
        PleaseSelectAWorkshop: "Veuillez sélectionner un atelier",
		ClickAWorkshopForMoreInformation: "Cliquez sur un atelier pour plus d'informations",
        HelpPageMessage: "Pour plus d'informations, veuillez consulter notre ",
        HelpPageLinkText: "page d'aide",
        eEFPMessage: 
`
Le plan agroenvironnementaux (PAE) est une évaluation préparée volontairement par les familles agricoles pour accroître leur sensibilisation à l'environnement dans jusqu'à 23 domaines différents de leur ferme. Grâce au processus PAE, les agriculteurs mettent en évidence les forces environnementales de leur ferme, identifient les domaines de préoccupation environnementale et établir des plans d'action réalistes avec des échéanciers pour améliorer les conditions environnementales.

Le PAEelectronique est une option pratique pour les agriculteurs qui souhaitent mettre à jour leur cahier de travail PAE précédemment vérifié. Ceci ne remplace pas l'atelier de 2 jours en personne, et les agriculteurs qui découvrent le PAE ou qui n'ont pas participé depuis de nombreuses années sont encouragés à envisager l'option en personne qui fournit des instructions de l'OSCIA et un réseautage avec d'autres agriculteurs. Les agriculteurs peuvent également souhaiter apporter un ordinateur portable à un atelier en personne d'une journée ou de deux jours et remplir leur cahier de travail PAE en ligne à l'aide de le PAEelectronique.

Pour remplir un PAEelectronique, visitez 
`, 
        eEFPLinkText: "www.electronicefp.com",
        WorkshopLeader: "Chef d'atelier",
        SeatsAvailableTemplate: "{SeatsLeft} sièges disponibles sur {MaximumParticipants}",
        Register: "S'inscrire",
        Registered: "Inscrit",
        Unregister: "Se désinscrire",
        Tentative: "Provisoire",
        Scheduled: "Programmé",
        Completed: "Complété",
        Cancelled: "Annulé",
        InProgress: "En cours",
        Day: "Jour",
        Date: "Date",
        Start: "Démarrer",
        Finish: "Finir",
        End: "Finir",
        Room: "Salle",
        Status: "Statut",
        Farmland: "Les terres agricoles",
        SelectDateRange: "Sélectionnez la plage de dates",
        SelectStartDate: "Sélectionnez la date de début",
        SelectEndDate: "Sélectionnez la date de fin",
        WorkshopType: "Type d'atelier",
        SelectWorkshopType: "Sélectionnez le type d'atelier",
        SelectTier1: "Sélectionnez le niveau 1",
        SelectTier2: "Sélectionnez le niveau 2",
        CopyrightNoticeTemplate: "© Imprimeur de le Roi pour l'Ontario {year}",
        LoginRequired: "Connexion requise",
        LoginRequiredMessage: "Avant de pouvoir vous inscrire à un atelier, vous devez être connecté à OsciaPortal.org. Si vous n'avez pas de compte, vous devez en créer un.",
        ProceedToOntarioProgramsNet: "Rendez-vous sur OsciaPortal.org",
        Dashboard: "Tableau de bord",
        Portal: "Portail",
        Login: "Connexion",
        Logout: "Se déconnecter",
        Continue: "Continuez",
        NoWorkshopsAvailableWithCurrentFilterSelections: "Il n'y a pas d'ateliers programmés dans les critères que vous avez sélectionnés. Veuillez ajuster vos filtres pour réessayer votre recherche.",
		NoWorkshopsAvailableOfThisType: "Il n'y a pas d'ateliers de ce type actuellement programmés. Veuillez sélectionner un autre type d'atelier ou",
		ContactYourWorkshopLeaderLinkText: "contactez votre chef d'atelier",
        PleaseCorrectTheIndicatedErrors: "Merci de corriger les erreurs signalées",
        ThisWorkshopIsTentativelyScheduled: "Cet atelier est provisoirement programmé",
        SessionExpirationTitle: "Expiration de la session",
        SessionExpirationWarning: "Votre session est sur le point d'expirer. Si vous souhaitez continuer, veuillez appuyer sur 'Continuer'",
        List: "Lister",
        Calendar: "Calendrier",
        View: "Voir",
        Today: "Aujourd'hui",
        PrerequisiteRequired: "Besoin de prérequis",
        Confirm: "Confirmer",
        RegistrationCutoffNoticeTemplate: "Il ne reste plus que {friendlyDuration} pour s'inscrire",
		ParticipantDetails: "Détails des participants",
        FirstName: "Prénom",
        LastName: "Nom de famille",
        AddParticipant: "Ajouter un participant",
        Remove: "Retirer",
        RemoveParticipant: "Supprimer un participant",
        AdditionalParticipant: "Participant supplémentaire",
        PreviousGuests: "Invités précédents",
        Add: "Ajouter",
        AddNew: "Ajouter nouveau",
        Province: "Province",
        MunicipalityTier1: "Municipalité Niveau 1",
        MunicipalityTier2: "Municipalité Niveau 2",
		County: 'Comté',
		Municipality: 'Municipalité',
        AddressLine1: "Adresse 1",
        AddressLine2: "Adresse Ligne 2",
        City: "Ville",
        PostalCode: "code postal",
        Email: "Courriel",
        Phone1: "Téléphone 1",
        Phone2: "Téléphone 2",
        Ext: "poste",
        IsCertifiedOrganic: "Est certifié biologique",
        IsOrganicCertificationPending: "La certification biologique est-elle en attente",
        CommodityCategory: "Catégorie de marchandise",
        CommoditySubCategory: "Sous-catégorie de produits",
        Watershed: "Bassin versant",
        PreferredCorrespondenceLanguage: "Langue de correspondance préférée",
        DoYouRequireAnyAccomodationsForAccessibility: "Avez-vous besoin d'aménagements pour l'accessibilité?",
        PreviouslyCompletedWorkbooks: "Cahiers déjà remplis",
        Cancel: "Annuler",
        RegistrationSuccessful: "Vous vous êtes inscrit avec succès à cet atelier",
        UnregistrationSuccessful: "Vous avez réussi à vous désinscrire de cet atelier",
        UnregistrationConfirmationTitle: "Confirmation de désinscription",
        UnregistrationConfirmationMessage: "Voulez-vous vraiment vous désinscrire de cet atelier ?",
        Admin: "Administrateur",
    }
}

